import React, {FC} from 'react';

import {Renderer} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {TeamModel} from '../../../../../libs/shared/core/src/models/api/teamModel';
import {globalNamespace} from '../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {CompanyDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/companyDropdownItem';
import {Dropdown, DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {TeamDropdownItemContainer} from '../../../../../libs/shared/ui/src/components/dropdowns/teamDropdownItemContainer';
import {SharedDropdownItem} from '../sharedDropdownItem';
import {UserDropdownItem} from '../userDropdownItem';

/*
 * Props.
 */

interface NamespaceDropdownProps extends DropdownProps {
  selectedNamespace: string | undefined;
  /** If undefined, the user namespace will not be a visible choice. */
  userNamespace: string | undefined;
  teams: ReadonlyArray<TeamModel>;
  onNamespaceSelect: (arg: string) => void;
  renderUserItemName?: Renderer;
  shouldRenderCompanyDropdownItem?: boolean;
  shouldRenderSharedDropdownItem?: boolean;
  shouldRenderVisibilityDescription?: boolean;
  /** Should user namespace appear in first or last position? */
  shouldPrioritizeUser?: boolean;
  header?: React.ReactNode;
}

/*
 * Component.
 */

export const NamespaceDropdown: FC<NamespaceDropdownProps> = (props) => (
  <Dropdown {...props}>
    {props.header && <DropdownHeader title={props.header} />}
    <DropdownList {...props}>{renderItems(props)}</DropdownList>
  </Dropdown>
);

function renderItems(props: NamespaceDropdownProps) {
  const {shouldPrioritizeUser} = props;

  if (shouldPrioritizeUser) {
    return (
      <>
        {renderUserItem(props)}
        {renderTeamItems(props)}
        {renderSharedItem(props)}
        {renderCompanyItem(props)}
      </>
    );
  }

  return (
    <>
      {renderCompanyItem(props)}
      {renderSharedItem(props)}
      {renderTeamItems(props)}
      {renderUserItem(props)}
    </>
  );
}

function renderUserItem(props: NamespaceDropdownProps) {
  const {
    userNamespace,
    selectedNamespace,
    onNamespaceSelect,
    renderUserItemName,
    shouldRenderVisibilityDescription,
  } = props;
  if (!userNamespace) {
    return null;
  }

  const isChecked = selectedNamespace === userNamespace;
  return (
    <UserDropdownItem
      isChecked={isChecked}
      onSelect={() => onNamespaceSelect(userNamespace)}
      renderUserItemName={renderUserItemName}
      shouldRenderVisibilityDescription={shouldRenderVisibilityDescription}
    />
  );
}

function renderTeamItems(props: NamespaceDropdownProps) {
  const {teams, selectedNamespace, onNamespaceSelect, shouldRenderVisibilityDescription} = props;
  return teams.map((t) => (
    <TeamDropdownItemContainer
      key={t.id}
      team={t}
      isChecked={selectedNamespace === t.namespace}
      onSelect={() => onNamespaceSelect(t.namespace)}
      shouldRenderVisibilityDescription={shouldRenderVisibilityDescription}
    />
  ));
}

function renderCompanyItem(props: NamespaceDropdownProps) {
  const {
    shouldRenderCompanyDropdownItem,
    selectedNamespace,
    onNamespaceSelect,
    shouldRenderVisibilityDescription,
  } = props;
  if (!shouldRenderCompanyDropdownItem) {
    return null;
  }

  const isChecked = selectedNamespace === globalNamespace;
  return (
    <CompanyDropdownItem
      isChecked={isChecked}
      onSelect={() => onNamespaceSelect(globalNamespace)}
      shouldRenderVisibilityDescription={shouldRenderVisibilityDescription}
    />
  );
}

function renderSharedItem(props: NamespaceDropdownProps) {
  const {
    shouldRenderSharedDropdownItem,
    selectedNamespace,
    onNamespaceSelect,
    shouldRenderVisibilityDescription,
  } = props;
  if (!shouldRenderSharedDropdownItem) {
    return null;
  }

  const isChecked = selectedNamespace === globalNamespace;
  return (
    <SharedDropdownItem
      isChecked={isChecked}
      onSelect={() => onNamespaceSelect(globalNamespace)}
      shouldRenderVisibilityDescription={shouldRenderVisibilityDescription}
    />
  );
}
